import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CircularProgress,
  IconButton,
  InputBase,
  makeStyles,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CloseIcon from "@material-ui/icons/Close";
import ConfirmationNumberOutlinedIcon from "@material-ui/icons/ConfirmationNumberOutlined";
import DoneIcon from "@material-ui/icons/Done";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import InsertDriveFileOutlinedIcon from "@material-ui/icons/InsertDriveFileOutlined";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import MoodIcon from "@material-ui/icons/Mood";
import SendIcon from "@material-ui/icons/Send";
import "emoji-mart/css/emoji-mart.css";
import { Picker } from "emoji-mart";

import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";
import ChatAvatar, { UserAvatar } from "./ChatAvatar";
import ChatMedia from "./ChatMedia";
import TicketPicker from "./TicketPicker";
import {
  CHAT_MAX_FILE_SIZE,
  TICKET_STATUS_LABEL,
  formatDayLabel,
  formatFileSize,
  formatTime,
  getChatTitle,
  isDirectChat,
  isSameDay,
  otherMembers,
  readersOf,
  splitMessage,
  userNameColor,
} from "./chatUtils";

// Mensagens da mesma pessoa com até 5 min de intervalo formam um bloco
const GROUP_GAP_MS = 5 * 60 * 1000;

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  return {
    root: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      minHeight: 0,
      color: theme.palette.text.primary,
    },
    header: {
      height: 64,
      flexShrink: 0,
      boxSizing: "border-box",
      padding: theme.spacing(0, 1, 0, 2.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      [theme.breakpoints.down("sm")]: { padding: theme.spacing(0, 0.5) },
    },
    headerText: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: 2,
    },
    headerTitle: {
      fontSize: 15,
      fontWeight: 700,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    headerSub: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    online: {
      color: isDark ? "#7FD6A6" : "#157A47",
      fontWeight: 600,
    },
    stack: {
      display: "flex",
      alignItems: "center",
      marginRight: theme.spacing(0.5),
      "& > *": {
        borderRadius: "50%",
        boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
      },
      "& > * + *": { marginLeft: -8 },
      [theme.breakpoints.down("sm")]: { display: "none" },
    },
    more: {
      width: 30,
      height: 30,
      borderRadius: "50%",
      backgroundColor: theme.palette.action.selected,
      color: theme.palette.text.secondary,
      fontSize: 11,
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    body: {
      position: "relative",
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
    },
    list: {
      position: "relative",
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      padding: theme.spacing(2, 3),
      backgroundColor: isDark ? "#1A2035" : "#EEF2F7",
      ...theme.scrollbarStylesSoft,
      [theme.breakpoints.down("sm")]: { padding: theme.spacing(1.5) },
    },
    loadingTop: {
      display: "flex",
      justifyContent: "center",
      padding: theme.spacing(1),
    },
    day: {
      display: "flex",
      justifyContent: "center",
      padding: theme.spacing(1.5, 0),
    },
    dayLabel: {
      fontSize: 11,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 12,
      padding: "4px 12px",
    },
    unreadDivider: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.25, 0),
      "&::before, &::after": {
        content: '""',
        flex: 1,
        height: 1,
        backgroundColor: "#F0A35E",
      },
    },
    unreadLabel: {
      fontSize: 11,
      fontWeight: 700,
      color: isDark ? "#F5B27A" : "#A04A08",
    },
    row: {
      display: "flex",
      alignItems: "flex-end",
      gap: theme.spacing(1),
    },
    rowOwn: { justifyContent: "flex-end" },
    avatarSlot: { width: 32, flexShrink: 0 },
    bubble: {
      maxWidth: "min(520px, 75%)",
      padding: "7px 12px 5px",
      borderRadius: 12,
      boxSizing: "border-box",
      wordBreak: "break-word",
    },
    bubbleIn: {
      backgroundColor: isDark ? "#2A3A52" : "#FFFFFF",
      boxShadow: isDark ? "none" : "0 1px 1px rgba(16,24,40,0.06)",
    },
    bubbleOwn: {
      backgroundColor: "#2468D4",
      color: "#FFFFFF",
    },
    tailIn: { borderBottomLeftRadius: 4 },
    tailOwn: { borderBottomRightRadius: 4 },
    sender: {
      fontSize: 12,
      fontWeight: 700,
      marginBottom: 2,
    },
    text: {
      fontSize: 14,
      lineHeight: 1.45,
      whiteSpace: "pre-wrap",
    },
    time: {
      fontSize: 11,
      textAlign: "right",
      marginTop: 2,
      color: theme.palette.text.secondary,
    },
    timeOwn: { color: "rgba(255,255,255,0.88)" },
    jump: {
      position: "absolute",
      right: theme.spacing(3),
      bottom: theme.spacing(2),
      backgroundColor: theme.palette.background.paper,
      boxShadow: "0 4px 12px rgba(16,24,40,0.18)",
      "&:hover": { backgroundColor: theme.palette.background.paper },
    },
    composer: {
      flexShrink: 0,
      borderTop: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1.5, 2),
      display: "flex",
      flexDirection: "column",
      gap: 6,
      [theme.breakpoints.down("sm")]: { padding: theme.spacing(1) },
    },
    composerRow: {
      display: "flex",
      alignItems: "flex-end",
      gap: theme.spacing(1),
    },
    emojiWrap: { position: "relative" },
    emojiBox: {
      position: "absolute",
      bottom: 48,
      left: 0,
      zIndex: 10,
    },
    inputBox: {
      flex: 1,
      minHeight: 40,
      display: "flex",
      alignItems: "center",
      padding: theme.spacing(0, 1.75),
      borderRadius: 20,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#F8FAFC",
      "&:focus-within": { borderColor: theme.palette.primary.main },
    },
    input: {
      width: "100%",
      fontSize: 14,
      padding: "9px 0",
      color: theme.palette.text.primary,
    },
    send: {
      width: 40,
      height: 40,
      backgroundColor: "#2468D4",
      color: "#fff",
      "&:hover": { backgroundColor: "#1A4FA8" },
      "&.Mui-disabled": {
        backgroundColor: theme.palette.action.disabledBackground,
        color: theme.palette.action.disabled,
      },
    },
    hint: {
      fontSize: 11,
      color: theme.palette.text.secondary,
      paddingLeft: 52,
      [theme.breakpoints.down("sm")]: { display: "none" },
    },
    bubbleCol: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      gap: 3,
      maxWidth: "min(520px, 75%)",
      "& > $bubble": { maxWidth: "100%" },
    },
    meta: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 4,
      marginTop: 2,
    },
    check: { fontSize: 15, color: "rgba(255,255,255,0.88)" },
    seen: {
      fontSize: 11,
      color: theme.palette.text.secondary,
    },
    mention: {
      fontWeight: 600,
      borderRadius: 4,
      padding: "0 2px",
      color: isDark ? "#8DB8F5" : "#1A4FA8",
      backgroundColor: isDark ? "rgba(45,123,232,0.18)" : "#E8F0FC",
    },
    mentionOwn: {
      color: "#FFFFFF",
      backgroundColor: "rgba(255,255,255,0.2)",
    },
    mentionMe: {
      color: isDark ? "#F5B27A" : "#8A3A00",
      backgroundColor: isDark ? "rgba(240,163,94,0.18)" : "#FDEBD7",
    },
    link: {
      color: "inherit",
      textDecoration: "underline",
      wordBreak: "break-all",
    },
    mediaBox: {
      marginTop: 4,
      marginBottom: 2,
    },
    ticketCard: {
      marginTop: 6,
      display: "flex",
      alignItems: "center",
      gap: 10,
      width: "100%",
      minWidth: 230,
      boxSizing: "border-box",
      padding: "8px 10px",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.14)" : "#D5DBE4"}`,
      borderRadius: 8,
      backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "#F8FAFC",
      color: theme.palette.text.primary,
      fontFamily: "inherit",
      textAlign: "left",
      cursor: "pointer",
      "&:hover": { borderColor: theme.palette.primary.main },
    },
    ticketCardOwn: {
      borderColor: "rgba(255,255,255,0.35)",
      backgroundColor: "rgba(255,255,255,0.12)",
      color: "#FFFFFF",
      "&:hover": { borderColor: "#FFFFFF" },
    },
    ticketText: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: 1,
    },
    ticketTitle: {
      fontSize: 13,
      fontWeight: 600,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    ticketSub: { fontSize: 11, opacity: 0.8 },
    ticketOpen: { fontSize: 12, fontWeight: 600 },
    ticketCardLocked: {
      cursor: "default",
      "&:hover": { borderColor: isDark ? "rgba(255,255,255,0.14)" : "#D5DBE4" },
    },
    ticketLocked: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      fontSize: 11,
      fontWeight: 600,
      opacity: 0.85,
      flexShrink: 0,
    },
    typing: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      paddingTop: 8,
      fontSize: 12,
      color: theme.palette.text.secondary,
    },
    dots: {
      display: "inline-flex",
      gap: 3,
      "& span": {
        width: 5,
        height: 5,
        borderRadius: "50%",
        backgroundColor: theme.palette.text.secondary,
        animation: "$blink 1.2s infinite ease-in-out",
      },
      "& span:nth-child(2)": { animationDelay: "0.2s" },
      "& span:nth-child(3)": { animationDelay: "0.4s" },
    },
    "@keyframes blink": {
      "0%, 80%, 100%": { opacity: 0.25 },
      "40%": { opacity: 1 },
    },
    attachment: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: theme.spacing(1, 1.5),
      borderRadius: 10,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#F8FAFC",
    },
    attachmentThumb: {
      width: 44,
      height: 44,
      borderRadius: 6,
      objectFit: "cover",
      flexShrink: 0,
    },
    attachmentText: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
    },
    attachmentName: {
      fontSize: 13,
      fontWeight: 600,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    attachmentSize: { fontSize: 11, color: theme.palette.text.secondary },
    mentionBox: {
      position: "absolute",
      left: 0,
      right: 0,
      bottom: "100%",
      marginBottom: 6,
      zIndex: 10,
      maxWidth: 320,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 10,
      boxShadow: "0 8px 24px rgba(16,24,40,0.16)",
      overflow: "hidden",
    },
    mentionItem: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      width: "100%",
      padding: theme.spacing(1, 1.5),
      border: 0,
      background: "transparent",
      fontFamily: "inherit",
      fontSize: 13,
      color: theme.palette.text.primary,
      textAlign: "left",
      cursor: "pointer",
    },
    mentionItemActive: {
      backgroundColor: isDark ? "rgba(45,123,232,0.18)" : "#E8F0FC",
    },
    inputWrap: { position: "relative", flex: 1, minWidth: 0, display: "flex" },
    dropOverlay: {
      position: "absolute",
      inset: 8,
      zIndex: 5,
      borderRadius: 12,
      border: `2px dashed ${theme.palette.primary.main}`,
      backgroundColor: isDark ? "rgba(26,32,53,0.85)" : "rgba(238,242,247,0.9)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 15,
      fontWeight: 600,
      color: theme.palette.text.primary,
      pointerEvents: "none",
    },
  };
});

const buildRows = (messages, firstUnreadId) => {
  const rows = [];
  messages.forEach((msg, i) => {
    const prev = messages[i - 1];
    const next = messages[i + 1];
    const newDay = !prev || !isSameDay(prev.createdAt, msg.createdAt);
    const isUnreadStart = msg.id === firstUnreadId;
    const joinsPrev =
      !!prev &&
      !newDay &&
      !isUnreadStart &&
      prev.senderId === msg.senderId &&
      new Date(msg.createdAt) - new Date(prev.createdAt) < GROUP_GAP_MS;
    const joinsNext =
      !!next &&
      isSameDay(msg.createdAt, next.createdAt) &&
      next.id !== firstUnreadId &&
      next.senderId === msg.senderId &&
      new Date(next.createdAt) - new Date(msg.createdAt) < GROUP_GAP_MS;

    if (newDay) {
      rows.push({ type: "day", key: `day-${msg.id}`, label: formatDayLabel(msg.createdAt) });
    }
    if (isUnreadStart) {
      rows.push({ type: "unread", key: "unread" });
    }
    rows.push({
      type: "msg",
      key: msg.id,
      msg,
      first: !joinsPrev,
      last: !joinsNext,
    });
  });
  return rows;
};

// Mesma regra da tela de atendimento e da API: admin vê tudo; os demais só
// atendimentos de um departamento (fila) a que pertencem.
const canOpenTicket = (ticket, user) => {
  if (!ticket) return false;
  if (user?.profile === "admin" || user?.super) return true;
  const queueId = ticket.queue?.id ?? ticket.queueId;
  return !!queueId && (user?.queues || []).some((q) => q.id === queueId);
};

const typingLabel = (names) => {
  if (names.length === 1) return `${names[0]} está digitando…`;
  if (names.length === 2) return `${names[0]} e ${names[1]} estão digitando…`;
  return "Várias pessoas estão digitando…";
};

const seenLabel = (readers, others, direct) => {
  if (!readers.length) return "";
  if (direct) return "Visto";
  if (readers.length >= others.length) return "Visto por todos";
  const names = readers.map((r) => (r.user?.name || "").split(" ")[0]);
  if (names.length <= 3) return `Visto por ${names.join(", ")}`;
  return `Visto por ${names.slice(0, 3).join(", ")} e +${names.length - 3}`;
};

// Cartão do atendimento citado. Quem não é do departamento vê o resumo, mas
// não o botão de abrir (a API também recusaria).
function TicketCard({ ticket, own, canOpen, onOpen, classes, children }) {
  const cls = `${classes.ticketCard} ${own ? classes.ticketCardOwn : ""}`;
  if (!canOpen) {
    const dept = ticket.queue?.name;
    return (
      <div
        className={`${cls} ${classes.ticketCardLocked}`}
        title={
          dept
            ? `Você não faz parte do departamento ${dept}. Peça para transferir o atendimento se precisar assumir.`
            : "Atendimento sem departamento: só administradores abrem."
        }
      >
        {children}
        <span className={classes.ticketLocked}>
          <LockOutlinedIcon style={{ fontSize: 14 }} />
          Sem acesso
        </span>
      </div>
    );
  }
  return (
    <button type="button" className={cls} onClick={onOpen}>
      {children}
      <span className={classes.ticketOpen}>Abrir</span>
    </button>
  );
}

export default function ChatMessages({
  chat,
  messages,
  hasMore,
  loading,
  firstUnreadId,
  unreadCount,
  typingNames = [],
  isMobile,
  onBack,
  onSendMessage,
  onTyping,
  onOpenTicket,
  onLoadMore,
  onEdit,
  onDelete,
}) {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const listRef = useRef();
  const inputRef = useRef();
  const emojiRef = useRef();
  const fileInputRef = useRef();
  const dragDepth = useRef(0);
  const snapshot = useRef({ firstId: null, lastId: null, height: 0, nearBottom: true });

  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [ticketRef, setTicketRef] = useState(null);
  const [pickerAnchor, setPickerAnchor] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [mention, setMention] = useState(null); // { query, start, end }
  const [mentionIndex, setMentionIndex] = useState(0);
  const [showEmoji, setShowEmoji] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [showJump, setShowJump] = useState(false);

  const theme = useTheme();
  const themeIsDark = theme.palette.type === "dark";

  const direct = isDirectChat(chat);
  const others = otherMembers(chat, user.id);
  const onlineOthers = others.filter((u) => u.user?.online).length;
  const isOwner = chat.ownerId === user.id;

  const memberNamesList = useMemo(
    () => (chat.users || []).map((u) => u.user?.name).filter(Boolean),
    [chat.users]
  );

  const rows = useMemo(
    () => buildRows(messages, firstUnreadId),
    [messages, firstUnreadId]
  );

  const lastOwnId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].senderId === user.id) return messages[i].id;
    }
    return null;
  }, [messages, user.id]);

  const mentionCandidates = useMemo(() => {
    if (!mention) return [];
    const q = mention.query.toLowerCase();
    return others
      .filter((u) => u.user?.name)
      .filter((u) =>
        u.user.name
          .toLowerCase()
          .split(/\s+/)
          .some((part) => part.startsWith(q)) ||
        u.user.name.toLowerCase().startsWith(q)
      )
      .slice(0, 6);
  }, [mention, others]);

  // Posição da rolagem: no fim ao abrir (ou na primeira não lida), mantida ao
  // carregar mensagens antigas, e acompanha as novas se já estava no fim.
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const s = snapshot.current;
    const firstId = messages[0]?.id ?? null;
    const lastId = messages[messages.length - 1]?.id ?? null;

    if (lastId === null) {
      s.firstId = null;
      s.lastId = null;
      s.height = el.scrollHeight;
      return;
    }

    if (s.lastId === null) {
      const divider = el.querySelector("[data-unread-divider]");
      el.scrollTop = divider
        ? Math.max(0, divider.offsetTop - 80)
        : el.scrollHeight;
    } else if (firstId !== s.firstId && lastId === s.lastId) {
      el.scrollTop += el.scrollHeight - s.height;
    } else if (lastId !== s.lastId) {
      const last = messages[messages.length - 1];
      if (s.nearBottom || last.senderId === user.id) {
        el.scrollTop = el.scrollHeight;
      } else {
        setShowJump(true);
      }
    }

    s.firstId = firstId;
    s.lastId = lastId;
    s.height = el.scrollHeight;
  }, [messages, user.id]);

  // "digitando…" aparece no fim da lista: acompanha se já estava lá
  useLayoutEffect(() => {
    const el = listRef.current;
    if (el && typingNames.length && snapshot.current.nearBottom) {
      el.scrollTop = el.scrollHeight;
    }
  }, [typingNames.length]);

  useEffect(() => {
    if (!isMobile && inputRef.current) inputRef.current.focus();
  }, [isMobile]);

  useEffect(() => {
    if (!showEmoji) return undefined;
    const handleOutside = (e) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target)) {
        setShowEmoji(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [showEmoji]);

  // miniatura do anexo de imagem antes de enviar
  useEffect(() => {
    if (!file || !file.type.startsWith("image/")) {
      setFilePreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setFilePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleScroll = (e) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    snapshot.current.nearBottom = nearBottom;
    if (nearBottom && showJump) setShowJump(false);
    if (el.scrollTop < 200 && hasMore && !loading) onLoadMore();
  };

  const scrollToBottom = () => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    setShowJump(false);
  };

  const pickFile = (picked) => {
    if (!picked) return;
    if (picked.size > CHAT_MAX_FILE_SIZE) {
      toast.error("O arquivo deve ter no máximo 20 MB.");
      return;
    }
    setFile(picked);
    if (inputRef.current) inputRef.current.focus();
  };

  const send = async () => {
    const value = text.trim();
    if ((!value && !file && !ticketRef) || sending) return;
    setSending(true);
    try {
      await onSendMessage(value, file, ticketRef ? ticketRef.id : null);
      setText("");
      setFile(null);
      setTicketRef(null);
      setMention(null);
      snapshot.current.nearBottom = true;
    } catch (err) {
      // o erro já aparece em toast; o rascunho fica para tentar de novo
    }
    setSending(false);
  };

  const updateMention = (value, caret) => {
    if (direct) {
      setMention(null);
      return;
    }
    const before = value.slice(0, caret);
    const match = /(?:^|\s)@([^\s@]{0,30})$/.exec(before);
    if (match) {
      setMention({
        query: match[1],
        start: caret - match[1].length - 1,
        end: caret,
      });
      setMentionIndex(0);
    } else {
      setMention(null);
    }
  };

  const handleChange = (e) => {
    const { value, selectionStart } = e.target;
    setText(value);
    updateMention(value, selectionStart ?? value.length);
    if (value.trim()) onTyping();
  };

  const selectMention = (member) => {
    if (!mention || !member?.user?.name) return;
    const insert = `@${member.user.name} `;
    const next = text.slice(0, mention.start) + insert + text.slice(mention.end);
    const caret = mention.start + insert.length;
    setText(next);
    setMention(null);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(caret, caret);
      }
    });
  };

  const handleKeyDown = (e) => {
    if (mention && mentionCandidates.length) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionIndex((i) => (i + 1) % mentionCandidates.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionIndex(
          (i) => (i - 1 + mentionCandidates.length) % mentionCandidates.length
        );
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        selectMention(mentionCandidates[mentionIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMention(null);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData?.files;
    if (pasted && pasted.length) {
      e.preventDefault();
      pickFile(pasted[0]);
    }
  };

  const hasFiles = (e) =>
    Array.from(e.dataTransfer?.types || []).includes("Files");

  const dragHandlers = {
    onDragEnter: (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current += 1;
      setDragging(true);
    },
    onDragOver: (e) => {
      if (hasFiles(e)) e.preventDefault();
    },
    onDragLeave: (e) => {
      if (!hasFiles(e)) return;
      dragDepth.current -= 1;
      if (dragDepth.current <= 0) {
        dragDepth.current = 0;
        setDragging(false);
      }
    },
    onDrop: (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      pickFile(e.dataTransfer.files[0]);
    },
  };

  const addEmoji = (emoji) => {
    const el = inputRef.current;
    const native = emoji.native || "";
    if (!el) {
      setText((t) => t + native);
      return;
    }
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    const next = text.slice(0, start) + native + text.slice(end);
    setText(next);
    setShowEmoji(false);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + native.length, start + native.length);
    });
  };

  const renderText = (message, own) =>
    splitMessage(message, memberNamesList).map((part, i) => {
      if (part.type === "mention") {
        const isMe =
          part.value.slice(1).toLowerCase() === (user.name || "").toLowerCase();
        const cls = own
          ? classes.mentionOwn
          : isMe
          ? classes.mentionMe
          : "";
        return (
          <span key={i} className={`${classes.mention} ${cls}`}>
            {part.value}
          </span>
        );
      }
      if (part.type === "link") {
        return (
          <a
            key={i}
            href={part.value}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.link}
          >
            {part.value}
          </a>
        );
      }
      return <React.Fragment key={i}>{part.value}</React.Fragment>;
    });

  const typingNow = typingNames.length > 0;

  const subtitle = direct ? (
    typingNow ? (
      <span className={classes.online}>digitando…</span>
    ) : others[0]?.user?.online ? (
      <span className={classes.online}>online</span>
    ) : (
      "offline"
    )
  ) : (
    <>
      {chat.users?.length || 0} participantes
      {onlineOthers > 0 && (
        <>
          {" · "}
          <span className={classes.online}>{onlineOthers} online</span>
        </>
      )}
    </>
  );

  const memberNames = (chat.users || [])
    .map((u) => (u.userId === user.id ? "Você" : u.user?.name))
    .filter(Boolean)
    .join(", ");

  const stackMembers = others.slice(0, 3);
  const extra = others.length - stackMembers.length;
  const canSend = (text.trim() || file || ticketRef) && !sending;

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        {isMobile && (
          <IconButton onClick={onBack} aria-label="Voltar para conversas">
            <ArrowBackIcon />
          </IconButton>
        )}
        <ChatAvatar chat={chat} userId={user.id} size={40} />
        <div className={classes.headerText}>
          <span className={classes.headerTitle}>
            {getChatTitle(chat, user.id)}
          </span>
          <span className={classes.headerSub}>{subtitle}</span>
        </div>
        {!direct && others.length > 0 && (
          <Tooltip title={memberNames}>
            <div className={classes.stack} aria-label={`Participantes: ${memberNames}`}>
              {stackMembers.map((m) => (
                <UserAvatar
                  key={m.userId}
                  id={m.userId}
                  name={m.user?.name}
                  size={30}
                />
              ))}
              {extra > 0 && <span className={classes.more}>+{extra}</span>}
            </div>
          </Tooltip>
        )}
        {isOwner && (
          <>
            <IconButton
              aria-label="Mais opções"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              {!direct && (
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    onEdit(chat);
                  }}
                >
                  Editar grupo
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  onDelete(chat);
                }}
              >
                Excluir conversa
              </MenuItem>
            </Menu>
          </>
        )}
      </div>

      <div className={classes.body} {...dragHandlers}>
        {dragging && (
          <div className={classes.dropOverlay}>Solte o arquivo para anexar</div>
        )}
        <div ref={listRef} className={classes.list} onScroll={handleScroll}>
          {loading && (
            <div className={classes.loadingTop}>
              <CircularProgress size={20} />
            </div>
          )}

          {!loading && messages.length === 0 && (
            <Typography
              variant="body2"
              color="textSecondary"
              align="center"
              style={{ padding: 32 }}
            >
              Nenhuma mensagem ainda. Diga um oi!
            </Typography>
          )}

          {rows.map((row) => {
            if (row.type === "day") {
              return (
                <div key={row.key} className={classes.day}>
                  <span className={classes.dayLabel}>{row.label}</span>
                </div>
              );
            }
            if (row.type === "unread") {
              return (
                <div
                  key={row.key}
                  className={classes.unreadDivider}
                  data-unread-divider
                >
                  <span className={classes.unreadLabel}>
                    {unreadCount === 1
                      ? "1 nova mensagem"
                      : `${unreadCount} novas mensagens`}
                  </span>
                </div>
              );
            }

            const { msg, first, last } = row;
            const own = msg.senderId === user.id;
            const showAvatar = !own && !direct;
            const readers = own ? readersOf(chat, msg) : [];
            const readByAll =
              own && others.length > 0 && readers.length >= others.length;
            const seen =
              own && msg.id === lastOwnId
                ? seenLabel(readers, others, direct)
                : "";

            const bubble = (
              <div
                className={`${classes.bubble} ${
                  own ? classes.bubbleOwn : classes.bubbleIn
                } ${last ? (own ? classes.tailOwn : classes.tailIn) : ""}`}
              >
                {!own && !direct && first && (
                  <div
                    className={classes.sender}
                    style={{
                      color: userNameColor(msg.senderId, themeIsDark),
                    }}
                  >
                    {msg.sender?.name}
                  </div>
                )}
                {msg.mediaUrl && (
                  <div className={classes.mediaBox}>
                    <ChatMedia message={msg} own={own} />
                  </div>
                )}
                {msg.message && (
                  <div className={classes.text}>
                    {renderText(msg.message, own)}
                  </div>
                )}
                {!msg.message && !msg.mediaUrl && !msg.ticket && (
                  <div className={classes.text} style={{ fontStyle: "italic", opacity: 0.75 }}>
                    Mensagem sem conteúdo
                  </div>
                )}
                {msg.ticket && (
                  <TicketCard
                    ticket={msg.ticket}
                    own={own}
                    canOpen={canOpenTicket(msg.ticket, user)}
                    onOpen={() => onOpenTicket(msg.ticket)}
                    classes={classes}
                  >
                    <ConfirmationNumberOutlinedIcon fontSize="small" />
                    <span className={classes.ticketText}>
                      <span className={classes.ticketTitle}>
                        Atendimento #{msg.ticket.id}
                        {msg.ticket.contact?.name
                          ? ` · ${msg.ticket.contact.name}`
                          : ""}
                      </span>
                      <span className={classes.ticketSub}>
                        {[
                          msg.ticket.queue?.name,
                          TICKET_STATUS_LABEL[msg.ticket.status] ||
                            msg.ticket.status,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                  </TicketCard>
                )}
                <div className={classes.meta}>
                  <span
                    className={`${classes.time} ${own ? classes.timeOwn : ""}`}
                    style={{ marginTop: 0 }}
                  >
                    {formatTime(msg.createdAt)}
                  </span>
                  {own &&
                    (readByAll ? (
                      <DoneAllIcon
                        className={classes.check}
                        titleAccess={direct ? "Lida" : "Lida por todos"}
                      />
                    ) : (
                      <DoneIcon className={classes.check} titleAccess="Enviada" />
                    ))}
                </div>
              </div>
            );

            return (
              <div
                key={row.key}
                className={`${classes.row} ${own ? classes.rowOwn : ""}`}
                style={{ marginTop: first ? 8 : 2 }}
              >
                {showAvatar && (
                  <span className={classes.avatarSlot}>
                    {last && (
                      <UserAvatar
                        id={msg.senderId}
                        name={msg.sender?.name}
                        size={32}
                      />
                    )}
                  </span>
                )}
                {own ? (
                  <div className={classes.bubbleCol}>
                    {bubble}
                    {seen && <span className={classes.seen}>{seen}</span>}
                  </div>
                ) : (
                  bubble
                )}
              </div>
            );
          })}

          {typingNow && (
            <div className={classes.typing} aria-live="polite">
              <span className={classes.dots} aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
              {typingLabel(typingNames)}
            </div>
          )}
        </div>

        {showJump && (
          <IconButton
            className={classes.jump}
            onClick={scrollToBottom}
            aria-label="Ir para a última mensagem"
          >
            <ArrowDownwardIcon />
          </IconButton>
        )}
      </div>

      <div className={classes.composer}>
        {file && (
          <div className={classes.attachment}>
            {filePreview ? (
              <img src={filePreview} alt="" className={classes.attachmentThumb} />
            ) : (
              <InsertDriveFileOutlinedIcon color="action" />
            )}
            <span className={classes.attachmentText}>
              <span className={classes.attachmentName}>{file.name}</span>
              <span className={classes.attachmentSize}>
                {formatFileSize(file.size)}
              </span>
            </span>
            <IconButton
              size="small"
              aria-label="Remover anexo"
              onClick={() => setFile(null)}
              disabled={sending}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        )}
        {ticketRef && (
          <div className={classes.attachment}>
            <ConfirmationNumberOutlinedIcon color="action" />
            <span className={classes.attachmentText}>
              <span className={classes.attachmentName}>
                Atendimento #{ticketRef.id} · {ticketRef.contact?.name || "Contato"}
              </span>
              <span className={classes.attachmentSize}>
                {[
                  ticketRef.queue?.name,
                  TICKET_STATUS_LABEL[ticketRef.status] || ticketRef.status,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </span>
            <IconButton
              size="small"
              aria-label="Remover atendimento"
              onClick={() => setTicketRef(null)}
              disabled={sending}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        )}
        <div className={classes.composerRow}>
          {/* no celular o teclado já tem emojis: sobra espaço para o texto */}
          {!isMobile && (
          <div className={classes.emojiWrap} ref={emojiRef}>
            <IconButton
              aria-label="Emoji"
              onClick={() => setShowEmoji((v) => !v)}
            >
              <MoodIcon />
            </IconButton>
            {showEmoji && (
              <div className={classes.emojiBox}>
                <Picker
                  perLine={isMobile ? 8 : 12}
                  showPreview={false}
                  showSkinTones={false}
                  onSelect={addEmoji}
                />
              </div>
            )}
          </div>
          )}
          <IconButton
            aria-label="Anexar arquivo"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={sending}
          >
            <AttachFileIcon />
          </IconButton>
          <Tooltip title="Citar atendimento">
            <span>
              <IconButton
                aria-label="Citar atendimento"
                onClick={(e) => setPickerAnchor(e.currentTarget)}
                disabled={sending}
              >
                <ConfirmationNumberOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <TicketPicker
            open={Boolean(pickerAnchor)}
            anchorEl={pickerAnchor}
            onClose={() => {
              setPickerAnchor(null);
              if (inputRef.current) inputRef.current.focus();
            }}
            onSelect={setTicketRef}
          />
          <input
            ref={fileInputRef}
            type="file"
            hidden
            onChange={(e) => {
              pickFile(e.target.files && e.target.files[0]);
              e.target.value = "";
            }}
          />
          <div className={classes.inputWrap}>
            {mention && mentionCandidates.length > 0 && (
              <div className={classes.mentionBox} role="listbox" aria-label="Mencionar">
                {mentionCandidates.map((m, i) => (
                  <button
                    type="button"
                    key={m.userId}
                    role="option"
                    aria-selected={i === mentionIndex}
                    className={`${classes.mentionItem} ${
                      i === mentionIndex ? classes.mentionItemActive : ""
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selectMention(m);
                    }}
                  >
                    <UserAvatar id={m.userId} name={m.user.name} size={26} />
                    {m.user.name}
                  </button>
                ))}
              </div>
            )}
            <label className={classes.inputBox}>
              <InputBase
                inputRef={inputRef}
                className={classes.input}
                multiline
                rowsMax={5}
                value={text}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onBlur={() => setMention(null)}
                placeholder={
                  file
                    ? "Adicione uma legenda (opcional)"
                    : ticketRef
                    ? "Adicione um comentário (opcional)"
                    : isMobile
                    ? "Mensagem"
                    : direct
                    ? "Escreva uma mensagem"
                    : "Escreva uma mensagem… use @ para mencionar"
                }
                inputProps={{ "aria-label": "Mensagem" }}
              />
            </label>
          </div>
          <IconButton
            className={classes.send}
            onClick={send}
            disabled={!canSend}
            aria-label="Enviar"
          >
            {sending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <SendIcon style={{ fontSize: 18 }} />
            )}
          </IconButton>
        </div>
        <span className={classes.hint}>
          Enter envia · Shift+Enter quebra linha
        </span>
      </div>
    </div>
  );
}
